import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useContent } from "@/lib/content";

const TAB_IDS = ["about", "resume", "training", "services", "testimonials", "contact"];

export const Navbar = () => {
  const { header } = useContent();
  const [active, setActive] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-35% 0px -60% 0px" }
    );
    TAB_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-ivory/85 backdrop-blur-md border-b border-champagne/60"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 h-16 md:h-20 flex items-center gap-6 md:gap-10">
        <a
          href="#top"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          data-testid="nav-logo"
          className="shrink-0 font-serif text-xl md:text-2xl font-semibold tracking-wide text-burgundy"
        >
          {header.logoLeft}<span className="text-rose">·</span>{header.logoRight}
        </a>
        <nav className="flex-1 flex items-center gap-1 md:gap-2 overflow-x-auto" data-testid="nav-tabs">
          {TAB_IDS.map((id) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={go(id)}
              data-testid={`nav-tab-${id}`}
              className={`relative whitespace-nowrap px-3 md:px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 ${
                active === id ? "text-burgundy" : "text-charcoal/60 hover:text-burgundy"
              }`}
            >
              {header.tabs[id]}
              <span
                aria-hidden="true"
                className={`absolute left-3 right-3 bottom-0 h-0.5 bg-burgundy origin-left transition-transform duration-300 ${
                  active === id ? "scale-x-100" : "scale-x-0"
                }`}
              />
            </a>
          ))}
          <Link
            to="/resources"
            data-testid="nav-tab-resources"
            className="relative whitespace-nowrap px-3 md:px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal/60 hover:text-burgundy transition-colors duration-300"
          >
            {header.tabs.resources || "Resources"}
          </Link>
        </nav>
      </div>
    </motion.header>
  );
};
